import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Chip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";

import CodingOppsAnalysisStep from "./CodingOppsAnalysisStep";

export default function IAAssistTab({
  patient,
  codingOpportunities,
  allNotes,
  labs,
  loading,
  advancedMdPatientId,
  // Estado de análisis de notas (viene del tab Clinical Notes)
  notesAnalysisPerNote,
  codingOppsAnalysis,
  setCodingOppsAnalysis,
}) {
  // Dialog state for viewing all findings/medications
  const [evidenceDialog, setEvidenceDialog] = useState({ open: false, type: null });

  const opsCount = codingOpportunities?.length || 0;
  const labsCount = labs?.length || 0;
  const analyzedNotesCount = Object.keys(notesAnalysisPerNote || {}).length;

  // Combinar todos los hallazgos de las notas analizadas
  const combinedNotesAnalysis = useMemo(() => {
    const analyses = Object.values(notesAnalysisPerNote || {});

    // Combinar todos los findings de todas las notas
    const allFindings = [];
    const allMedications = [];

    analyses.forEach(analysis => {
      if (analysis.findings) {
        analysis.findings.forEach(f => {
          allFindings.push({
            ...f,
            sourceNoteId: analysis.noteId,
            sourceNoteType: analysis.noteType,
            sourceNoteDate: analysis.noteDate,
          });
        });
      }
      if (analysis.medications) {
        analysis.medications.forEach(m => {
          allMedications.push({
            ...m,
            sourceNoteId: analysis.noteId,
            sourceNoteType: analysis.noteType,
          });
        });
      }
    });

    return {
      totalNotesAnalyzed: analyses.length,
      findings: allFindings,
      medications: allMedications,
      notesSummaries: analyses.map(a => ({
        noteId: a.noteId,
        noteType: a.noteType,
        noteDate: a.noteDate,
        summary: a.summary,
      })),
    };
  }, [notesAnalysisPerNote]);

  // Validaciones iniciales
  if (!patient) {
    return (
      <Alert severity="warning">
        Select a patient to run AI Assist.
      </Alert>
    );
  }

  if (opsCount === 0) {
    return (
      <Alert severity="info">
        No coding opportunities found for this patient.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Alert severity="info">
        Loading patient data from AdvancedMD...
      </Alert>
    );
  }

  if (!advancedMdPatientId) {
    return (
      <Alert severity="warning">
        No AdvancedMD match for this patient. AI Assist needs AdvancedMD notes/labs.
      </Alert>
    );
  }

  if (analyzedNotesCount === 0) {
    return (
      <Alert severity="warning">
        No clinical notes have been analyzed yet. Go to the "Clinical Notes" tab and analyze at least one note first.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header con metadata */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: "grey.50" }}>
        <Typography variant="h6" fontWeight={900}>
          Coding AI Assist
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.7 }}>
          Evaluate coding opportunities using evidence from analyzed clinical notes.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
          <Chip size="small" label={`AdvancedMD ID: ${advancedMdPatientId}`} variant="outlined" />
          <Chip size="small" label={`${opsCount} Coding Opportunities`} color="primary" variant="outlined" />
          <Chip
            size="small"
            icon={<CheckCircleIcon />}
            label={`${analyzedNotesCount} Notes Analyzed`}
            color="success"
          />
          <Chip size="small" label={`${combinedNotesAnalysis.findings.length} Findings Extracted`} color="info" variant="outlined" />
          <Chip size="small" label={`${labsCount} Lab Results`} variant="outlined" />
        </Stack>
      </Paper>

      {/* Resumen de hallazgos disponibles */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 1 }}>
          <LocalHospitalIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
          Clinical Findings Available for Analysis
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
          These findings were extracted from the {analyzedNotesCount} analyzed note(s) and will be used to evaluate coding opportunities:
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {combinedNotesAnalysis.findings.slice(0, 12).map((f, idx) => (
            <Chip
              key={idx}
              size="small"
              label={f.condition}
              color={f.negated ? "warning" : "primary"}
              variant="outlined"
              sx={{ pointerEvents: "none" }}
            />
          ))}
          {combinedNotesAnalysis.findings.length > 12 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => setEvidenceDialog({ open: true, type: "findings" })}
              sx={{ height: 24, fontSize: "0.75rem" }}
            >
              View all {combinedNotesAnalysis.findings.length} findings
            </Button>
          )}
        </Stack>

        {combinedNotesAnalysis.medications.length > 0 && (
          <>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              <MedicationIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
              Medications Found:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {combinedNotesAnalysis.medications.slice(0, 8).map((m, idx) => (
                <Chip
                  key={idx}
                  size="small"
                  label={`${m.medication} (${m.treats})`}
                  variant="outlined"
                  color="secondary"
                  sx={{ pointerEvents: "none" }}
                />
              ))}
              {combinedNotesAnalysis.medications.length > 8 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<VisibilityIcon />}
                  onClick={() => setEvidenceDialog({ open: true, type: "medications" })}
                  sx={{ height: 24, fontSize: "0.75rem" }}
                >
                  View all {combinedNotesAnalysis.medications.length} medications
                </Button>
              )}
            </Stack>
          </>
        )}
      </Paper>

      {/* Dialog for viewing all findings or medications */}
      <Dialog
        open={evidenceDialog.open}
        onClose={() => setEvidenceDialog({ open: false, type: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {evidenceDialog.type === "findings" ? (
            <>
              <LocalHospitalIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              All Clinical Findings ({combinedNotesAnalysis.findings.length})
            </>
          ) : (
            <>
              <MedicationIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              All Medications ({combinedNotesAnalysis.medications.length})
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {evidenceDialog.type === "findings" && (
            <Stack spacing={1}>
              {combinedNotesAnalysis.findings.map((f, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderLeft: 4,
                    borderLeftColor: f.negated ? "warning.main" : "primary.main",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="small"
                      label={f.condition}
                      color={f.negated ? "warning" : "primary"}
                      sx={{ fontWeight: 600 }}
                    />
                    {f.negated && (
                      <Chip size="small" label="Negated" color="warning" variant="outlined" />
                    )}
                    {f.sourceNoteType && (
                      <Chip size="small" label={f.sourceNoteType} variant="outlined" color="info" />
                    )}
                    {f.sourceNoteDate && (
                      <Chip size="small" label={f.sourceNoteDate} variant="outlined" />
                    )}
                    {f.confidence && (
                      <Chip
                        size="small"
                        label={`${Math.round(f.confidence * 100)}% conf.`}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  {f.evidence && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic", opacity: 0.85 }}>
                      "{f.evidence}"
                    </Typography>
                  )}
                  {f.icd10_relevance && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      ICD-10 relevance: {f.icd10_relevance}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Stack>
          )}

          {evidenceDialog.type === "medications" && (
            <Stack spacing={1}>
              {combinedNotesAnalysis.medications.map((m, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderLeft: 4,
                    borderLeftColor: "secondary.main",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      size="small"
                      label={m.medication}
                      color="secondary"
                      sx={{ fontWeight: 600 }}
                    />
                    <Typography variant="body2">
                      treats: <strong>{m.treats}</strong>
                    </Typography>
                    {m.sourceNoteType && (
                      <Chip size="small" label={m.sourceNoteType} variant="outlined" color="info" />
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceDialog({ open: false, type: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Componente de análisis de Coding Opportunities */}
      <CodingOppsAnalysisStep
        patient={patient}
        codingOpportunities={codingOpportunities}
        combinedNotesAnalysis={combinedNotesAnalysis}
        labs={labs}
        codingOppsAnalysis={codingOppsAnalysis}
        setCodingOppsAnalysis={setCodingOppsAnalysis}
        disabled={false}
        advancedMdPatientId={advancedMdPatientId}
      />
    </Box>
  );
}
